// Copyright 2019 Sorama B.V.

import { PLATFORM } from "aurelia-framework";
import { TimelineEntryType } from "datatypes/SoramaWebApi";
import {
    IWorkflowModuleConfig,
    WorkflowModuleType,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowModuleConfig";
import { FarFieldAnalysisGuidanceCatalog } from "./../GuidanceCatalog/FarFieldAnalysisGuidanceCatalog";
import { NearFieldAnalysisGuidanceCatalog } from "./../GuidanceCatalog/NearFieldAnalysisGuidanceCatalog";
import { SpectralTimeAnalysisGuidanceCatalog } from "./../GuidanceCatalog/SpectralTimeAnalysisGuidanceCatalog";

export interface IAnalysisWorkflowModuleConfig extends IWorkflowModuleConfig {
    singleMeasurementAnalysis: boolean;
}

export class AnalysisWorkflowModuleCatalog {
    static AnalysisOverviewModule: IWorkflowModuleConfig = {
        name: "analysisoverview",
        modulePath: PLATFORM.moduleName("modules/workflows/Analysis/AnalysisOverview/AnalysisOverviewModule"),
        title: "Measurements List",
        moduleType: WorkflowModuleType.Analysis,
        requireOnline: true,
        requireSignIn: true,
        canCollapse: true,
        canExtend: false,
        isInadmissible: true,
    };

    static TimeModule: IAnalysisWorkflowModuleConfig = {
        name: "timeanalysis",
        modulePath: PLATFORM.moduleName("modules/workflows/Analysis/Time/TimeModule"),
        title: "Time",
        icon: "fas fa-hourglass-half",
        moduleType: WorkflowModuleType.GenericAnalysis,
        timelineEventType: TimelineEntryType.TimeAnalysis,
        canExtend: true,
        requireOnline: true,
        requireSignIn: true,
        help: SpectralTimeAnalysisGuidanceCatalog.SpectralTimeAnalysisOverviewModule,
        singleMeasurementAnalysis: false,
    };

    static SpectrumModule: IAnalysisWorkflowModuleConfig = {
        name: "spectrumanalysis",
        modulePath: PLATFORM.moduleName("modules/workflows/Analysis/Spectrum/SpectrumModule"),
        title: "Spectrum",
        icon: "fas fa-chart-bar",
        moduleType: WorkflowModuleType.GenericAnalysis,
        canExtend: true,
        requireOnline: true,
        requireSignIn: true,
        help: SpectralTimeAnalysisGuidanceCatalog.SpectralTimeAnalysisOverviewModule,
        timelineEventType: TimelineEntryType.SpectrumAnalysis,
        singleMeasurementAnalysis: false,
    };

    static SpectrogramModule: IAnalysisWorkflowModuleConfig = {
        name: "spectrogramanalysis",
        modulePath: PLATFORM.moduleName("modules/workflows/Analysis/Spectrogram/SpectrogramModule"),
        title: "Spectrogram",
        icon: "fas fa-outdent",
        moduleType: WorkflowModuleType.GenericAnalysis,
        timelineEventType: TimelineEntryType.SpectrogramAnalysis,
        canExtend: true,
        requireOnline: true,
        requireSignIn: true,
        help: SpectralTimeAnalysisGuidanceCatalog.SpectralTimeAnalysisOverviewModule,
        singleMeasurementAnalysis: true,
    };

    static FarfieldModule: IAnalysisWorkflowModuleConfig = {
        name: "farfieldanalysis",
        modulePath: PLATFORM.moduleName("modules/workflows/Analysis/Farfield/FarfieldModule"),
        title: "Far Field",
        icon: "fas fa-rss fa-rotate-180",
        timelineEventType: TimelineEntryType.FarfieldAnalysis,
        moduleType: WorkflowModuleType.FarFieldAnalysis,
        canExtend: true,
        requireOnline: true,
        requireSignIn: true,
        help: FarFieldAnalysisGuidanceCatalog.FarFieldAnalysisOverviewModule,
        singleMeasurementAnalysis: true,
    };

    static NearfieldModule: IAnalysisWorkflowModuleConfig = {
        name: "nearfieldanalysis",
        modulePath: PLATFORM.moduleName("modules/workflows/Analysis/Nearfield/NearfieldModule"),
        title: "Near Field",
        icon: "fas fa-chess-board",
        moduleType: WorkflowModuleType.NearFieldAnalysis,
        timelineEventType: TimelineEntryType.NearfieldAnalysis,
        canExtend: true,
        requireOnline: true,
        requireSignIn: true,
        help: NearFieldAnalysisGuidanceCatalog.NearFieldAnalysisOverviewModule,
        singleMeasurementAnalysis: true,
    };

    static PsychoAcousticsModule: IAnalysisWorkflowModuleConfig = {
        name: "psychoacoustics",
        modulePath: PLATFORM.moduleName("modules/workflows/Analysis/PsychoAcoustics/PsychoAcousticsModule"),
        title: "Psychoacoustics",
        icon: "far fa-head-side-brain",
        moduleType: WorkflowModuleType.PsychoAcousticsAnalysis,
        canExtend: false,
        requireOnline: true,
        requireSignIn: true,
        singleMeasurementAnalysis: false,
    };

    static ReportModule: IWorkflowModuleConfig = {
        name: "report",
        modulePath: PLATFORM.moduleName("modules/workflows/Analysis/Report/ReportModule"),
        title: "Report",
        moduleType: WorkflowModuleType.Report,
        requireOnline: true,
        requireSignIn: true,
        isDefaultExtended: true,
    };
}
